// DisclaimerScreen - App disclaimer
import React from 'react';
import { motion } from 'framer-motion';
import { Sun } from 'lucide-react';
import { Button } from '../common/Button';

export const DisclaimerScreen = ({ onAccept }) => {
    return (
        <motion.div
            className="fixed inset-0 z-[400] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

            {/* Content */}
            <motion.div
                className="relative w-full max-w-lg bg-gradient-to-br from-gray-900/95 via-purple-950/95 to-gray-900/95 backdrop-blur-xl border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden"
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: "spring", damping: 25 }}
            >
                {/* Top glow */}
                <div className="absolute -top-1 -left-1 -right-1 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />

                <div className="p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <motion.div
                            animate={{
                                rotate: [0, 10, -10, 0],
                                scale: [1, 1.1, 1]
                            }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            <Sun className="w-12 h-12 text-amber-400 mx-auto" />
                        </motion.div>
                        <h2 className="text-2xl sm:text-3xl font-serif text-amber-100 mt-4">
                            Welcome to Cosmic Compass!
                        </h2>
                    </div>

                    {/* Content */}
                    <div className="space-y-4 text-amber-100/90 text-sm sm:text-base leading-relaxed">
                        <p>
                            This application offers tarot readings for <strong className="text-amber-200">entertainment and personal reflection</strong>.
                            The interpretations provided are generated by an AI and are based on traditional tarot symbolism,
                            including card orientations (upright or reversed).
                        </p>

                        <p className="font-medium text-amber-200">Please remember:</p>

                        <ul className="list-disc list-inside space-y-2 text-amber-100/80">
                            <li>Tarot readings are <strong>not a substitute</strong> for professional advice (legal, financial, medical, psychological, etc.).</li>
                            <li>Decisions you make based on these readings are your own responsibility.</li>
                            <li>The future is not fixed, and you have the power to shape your own path.</li>
                            <li>This app is intended for users <strong>18 years of age or older</strong>.</li>
                        </ul>

                        <p className="pt-2 text-purple-300/80 italic">
                            By clicking "Accept & Continue", you acknowledge that you have read, understood, and agree to this disclaimer.
                        </p>
                    </div>

                    {/* Accept Button */}
                    <motion.div
                        className="mt-8"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={onAccept}
                            className="w-full text-lg py-4"
                        >
                            Accept & Continue
                        </Button>
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default DisclaimerScreen;
